'use client';

import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { Headphones, Phone, PhoneIncoming, Clock, TrendingUp, Plus,  } from 'lucide-react';

interface Agent {
  id: string;
  name: string;
  avatar: string;
  status: 'available' | 'on-call' | 'break' | 'offline';
  callsToday: number;
  avgHandle: string;
  satisfaction: number;
  queue: string;
}

interface QueueItem {
  id: string;
  caller: string;
  company: string;
  waitTime: string;
  priority: 'urgent' | 'high' | 'normal';
  type: 'inbound' | 'callback';
  issue: string;
}

const agents: Agent[] = [
  { id: 'a1', name: 'Layla Khalid', avatar: 'LK', status: 'on-call', callsToday: 34, avgHandle: '4m 12s', satisfaction: 96, queue: 'Technical Support' },
  { id: 'a2', name: 'Omar Saeed', avatar: 'OS', status: 'available', callsToday: 28, avgHandle: '3m 45s', satisfaction: 94, queue: 'Billing' },
  { id: 'a3', name: 'Nadia Hassan', avatar: 'NH', status: 'on-call', callsToday: 41, avgHandle: '5m 02s', satisfaction: 91, queue: 'Technical Support' },
  { id: 'a4', name: 'Khalid Mansoor', avatar: 'KM', status: 'break', callsToday: 22, avgHandle: '4m 30s', satisfaction: 89, queue: 'General Enquiry' },
  { id: 'a5', name: 'Sara Al-Farsi', avatar: 'SF', status: 'available', callsToday: 37, avgHandle: '3m 58s', satisfaction: 97, queue: 'Billing' },
  { id: 'a6', name: 'Rami Yousef', avatar: 'RY', status: 'offline', callsToday: 0, avgHandle: '—', satisfaction: 93, queue: 'General Enquiry' },
];

const queue: QueueItem[] = [
  { id: 'q1', caller: 'James Okafor', company: 'Meridian BPO', waitTime: '0:42', priority: 'urgent', type: 'inbound', issue: 'Service outage — VOIP lines down' },
  { id: 'q2', caller: 'Priya Sharma', company: 'CloudBridge Inc.', waitTime: '1:15', priority: 'high', type: 'callback', issue: 'Invoice dispute — overcharge query' },
  { id: 'q3', caller: 'Tom Hargreaves', company: 'Vantage Media', waitTime: '2:03', priority: 'normal', type: 'inbound', issue: 'Campaign performance review' },
  { id: 'q4', caller: 'Aisha Noor', company: 'Pinnacle Logistics', waitTime: '2:47', priority: 'normal', type: 'inbound', issue: 'Onboarding assistance request' },
];

const statusConfig = {
  'available': { label: 'Available', color: 'text-success', dot: 'bg-success' },
  'on-call':   { label: 'On Call',   color: 'text-primary', dot: 'bg-primary' },
  'break':     { label: 'Break',     color: 'text-warning', dot: 'bg-warning' },
  'offline':   { label: 'Offline',   color: 'text-muted-foreground', dot: 'bg-muted-foreground' },
};

const priorityConfig = {
  urgent: { label: 'Urgent', color: 'text-danger',  bg: 'bg-danger/10' },
  high:   { label: 'High',   color: 'text-warning', bg: 'bg-warning/10' },
  normal: { label: 'Normal', color: 'text-success', bg: 'bg-success/10' },
};

export default function BpoPage() {
  const [agentFilter, setAgentFilter] = useState<string>('all');

  const filteredAgents = agentFilter === 'all' ? agents : agents.filter(a => a.status === agentFilter);
  const onCallCount = agents.filter(a => a.status === 'on-call').length;
  const availableCount = agents.filter(a => a.status === 'available').length;
  const totalCallsToday = agents.reduce((s, a) => s + a.callsToday, 0);

  return (
    <AppLayout>
      <div className="min-h-screen bg-background px-4 sm:px-6 py-6 max-w-screen-2xl mx-auto">
        {/* Header */}
        <div className="flex items-start sm:items-center justify-between mb-6 gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-foreground">BPO Center</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Real-time agent monitoring, call queue, and SLA performance</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="flex items-center gap-1.5 text-xs text-success font-medium bg-success/10 px-2.5 sm:px-3 py-1.5 rounded-full">
              <span className="w-2 h-2 rounded-full bg-success animate-pulse" />
              Live
            </span>
            <button className="flex items-center gap-2 bg-primary text-primary-foreground px-3 sm:px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors">
              <Plus size={16} />
              <span className="hidden sm:inline">Add Agent</span>
            </button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
          {[
            { label: 'Agents On Call', value: onCallCount, sub: `${availableCount} available`, icon: Headphones, color: 'text-primary', bg: 'bg-primary/10' },
            { label: 'Queue Length', value: queue.length, sub: '1 urgent waiting', icon: PhoneIncoming, color: 'text-warning', bg: 'bg-warning/10' },
            { label: 'Calls Today', value: totalCallsToday, sub: '+12% vs yesterday', icon: Phone, color: 'text-success', bg: 'bg-success/10' },
            { label: 'Avg CSAT', value: '93.5%', sub: 'Customer satisfaction', icon: TrendingUp, color: 'text-amber-400', bg: 'bg-amber-400/10' },
          ].map((kpi) => (
            <div key={kpi.label} className="bg-card border border-border rounded-xl p-3 sm:p-4">
              <div className="flex items-center justify-between mb-2 sm:mb-3">
                <span className="text-xs sm:text-sm text-muted-foreground">{kpi.label}</span>
                <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-lg ${kpi.bg} flex items-center justify-center`}>
                  <kpi.icon size={16} className={kpi.color} />
                </div>
              </div>
              <p className="text-2xl font-bold text-foreground">{kpi.value}</p>
              <p className="text-xs text-muted-foreground mt-1">{kpi.sub}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Agent Status */}
          <div className="lg:col-span-2 bg-card border border-border rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <h2 className="font-semibold text-foreground">Agent Status</h2>
              <div className="flex gap-2">
                {(['all', 'available', 'on-call', 'break', 'offline'] as const).map((s) => (
                  <button
                    key={s}
                    onClick={() => setAgentFilter(s)}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium capitalize transition-colors ${agentFilter === s ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'}`}
                  >
                    {s === 'all' ? 'All' : s}
                  </button>
                ))}
              </div>
            </div>
            <div className="divide-y divide-border">
              {filteredAgents.map((agent) => {
                const sc = statusConfig[agent.status];
                return (
                  <div key={agent.id} className="flex items-center gap-4 px-5 py-3 hover:bg-muted/20 transition-colors">
                    <div className="relative flex-shrink-0">
                      <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center">
                        <span className="text-xs font-semibold text-primary">{agent.avatar}</span>
                      </div>
                      <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-card ${sc.dot}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground">{agent.name}</p>
                      <p className="text-xs text-muted-foreground">{agent.queue}</p>
                    </div>
                    <span className={`text-xs font-medium ${sc.color}`}>{sc.label}</span>
                    <div className="text-right hidden sm:block">
                      <p className="text-sm font-semibold text-foreground">{agent.callsToday}</p>
                      <p className="text-xs text-muted-foreground">calls</p>
                    </div>
                    <div className="text-right hidden md:block">
                      <p className="text-sm font-semibold text-foreground">{agent.avgHandle}</p>
                      <p className="text-xs text-muted-foreground">avg handle</p>
                    </div>
                    <div className="text-right hidden lg:block">
                      <p className="text-sm font-semibold text-foreground">{agent.satisfaction}%</p>
                      <p className="text-xs text-muted-foreground">CSAT</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Live Queue */}
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <h2 className="font-semibold text-foreground">Live Queue</h2>
              <span className="text-xs text-muted-foreground">{queue.length} waiting</span>
            </div>
            <div className="divide-y divide-border">
              {queue.map((item) => {
                const pc = priorityConfig[item.priority];
                return (
                  <div key={item.id} className="px-5 py-4">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div>
                        <p className="text-sm font-medium text-foreground">{item.caller}</p>
                        <p className="text-xs text-muted-foreground">{item.company}</p>
                      </div>
                      <span className={`flex-shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${pc.bg} ${pc.color}`}>{pc.label}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mb-2 line-clamp-1">{item.issue}</p>
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1 text-xs text-warning">
                        <Clock size={11} />
                        {item.waitTime} wait
                      </span>
                      <span className="text-xs text-muted-foreground capitalize">{item.type}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* SLA Performance */}
        <div className="mt-6 bg-card border border-border rounded-xl p-5">
          <h2 className="font-semibold text-foreground mb-4">SLA Performance — This Week</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'First Call Resolution', value: 78, target: 80, color: 'bg-warning' },
              { label: 'Avg Speed to Answer', value: 92, target: 90, color: 'bg-success' },
              { label: 'Abandonment Rate', value: 4, target: 5, color: 'bg-success', invert: true },
              { label: 'SLA Compliance', value: 96, target: 95, color: 'bg-success' },
            ].map((sla) => (
              <div key={sla.label}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-muted-foreground">{sla.label}</span>
                  <span className="text-xs font-semibold text-foreground">{sla.value}{sla.label.includes('Rate') ? '%' : '%'}</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${sla.color}`} style={{ width: `${Math.min(sla.value, 100)}%` }} />
                </div>
                <p className="text-xs text-muted-foreground mt-1">Target: {sla.target}%</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
